import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import { Plus, Search } from "lucide-react";
import { useOrgContext } from "../../../context/OrgContext";
import { useAuth } from "../../../context/AuthContext";
import { RefreshIconButton } from "../../../components/ui/RefreshButton";
import { useImageUpload } from "../../marketplace/hooks/useImageUpload";
import {
  useOrganizationEvents,
  useCreateEvent,
  useUpdateEvent,
  usePublishEvent,
  useStopEventRecurrence,
} from "../hooks/useEvents";
import { EventCard } from "./EventCard";
import { CreateEventDrawer } from "./CreateEventDrawer";
import { PublishEventDialog } from "./PublishEventDialog";
import { CancelConfirmDialog } from "../../challenges/components/CancelConfirmDialog";
import { DeleteDraftDialog } from "../../challenges/components/DeleteDraftDialog";
import { DraftSuccessView } from "../../challenges/components/DraftSuccessView";
import { StopRecurrenceDialog } from "../../challenges/components/StopRecurrenceDialog";
import type { Event, EventStatus } from "../types";
import { useLocale } from "../../../context/LanguageContext";
import { toBcp47 } from "../../../i18n";

// ─── Status Filter Tabs ──────────────────────────────────────────────────────

const STATUS_TABS: Array<{ labelKey: string; value: EventStatus | null }> = [
  { labelKey: "events.statusAll", value: null },
  { labelKey: "events.statusDraft", value: "DRAFT" },
  { labelKey: "events.statusUpcoming", value: "UPCOMING" },
  { labelKey: "events.statusActive", value: "ACTIVE" },
  { labelKey: "events.statusCompleted", value: "COMPLETED" },
  { labelKey: "events.statusCancelled", value: "CANCELLED" },
];

const VALID_TAB_VALUES = new Set<string>(["DRAFT", "UPCOMING", "ACTIVE", "COMPLETED", "CANCELLED"]);
// ─── Error Helpers ─────────────────────────────────────────────────────────

const ERROR_MAP: Array<[RegExp, string]> = [
  [/start date must be before end date/i, "events.errors.startBeforeEnd"],
  [/start date.*past/i, "events.errors.startInPast"],
  [/name.*required/i, "events.errors.nameRequired"],
  [/already exists/i, "events.errors.nameExists"],
  [/not authorized/i, "events.errors.notAuthorized"],
  [/image.*upload/i, "events.errors.imageUpload"],
  [/meeting.*link.*valid/i, "events.errors.meetingLinkInvalid"],
];

function getSubmitErrorKey(err: unknown): string {
  const raw =
    err instanceof Error ? err.message : String(err);

  for (const [pattern, key] of ERROR_MAP) {
    if (pattern.test(raw)) return key;
  }

  return "events.errors.generic";
}
// ─── Component ───────────────────────────────────────────────────────────────

export function EventsPage() {
  const { currentOrgId, currentOrg } = useOrgContext();
  const { user } = useAuth();
  const { t, locale } = useLocale();
  const isOrgAdmin = user?.role === "ORG_ADMIN";

  // ─── Search & Filter ──────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // ─── URL ↔ Tab Sync (deep-link support) ───────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab")?.toUpperCase();
  const initialTab: EventStatus | null =
    tabParam && VALID_TAB_VALUES.has(tabParam) ? (tabParam as EventStatus) : "ACTIVE";
  const [statusFilter, setStatusFilter] = useState<EventStatus | null>(initialTab);

  const handleTabChange = useCallback((value: EventStatus | null) => {
    setStatusFilter(value);
    setSearchParams(value ? { tab: value } : {}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ─── Data Fetching ────────────────────────────────────────────────────
  const { data, loading, refetch } = useOrganizationEvents({
    organizationId: currentOrgId ?? "",
    limit: 100,
  });

  const allEvents: Event[] = data?.organizationEvents?.items ?? [];
  const totalCount = data?.organizationEvents?.totalCount ?? 0;

  const filteredEvents = allEvents.filter((ev) => {
    if (statusFilter && ev.status !== statusFilter) return false;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      return ev.name.toLowerCase().includes(q);
    }
    return true;
  });

  // ─── Drawer State ─────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | undefined>();
  const [duplicateFrom, setDuplicateFrom] = useState<Event | undefined>();

  // ─── Publish Dialog ───────────────────────────────────────────────────
  const [publishTarget, setPublishTarget] = useState<Event | null>(null);

  // ─── Cancel Dialog ────────────────────────────────────────────────────
  const [cancelTarget, setCancelTarget] = useState<Event | null>(null);

  // ─── Stop Recurrence Dialog ───────────────────────────────────────────
  const [stopRecurrenceTarget, setStopRecurrenceTarget] = useState<Event | null>(null);

  // ─── Delete Draft Dialog ──────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<Event | null>(null);

  // ─── Draft Success State ──────────────────────────────────────────────
  const [createdDraft, setCreatedDraft] = useState<Event | null>(null);
  const [draftVariant, setDraftVariant] = useState<"created" | "updated">("created");

  // ─── Submit Error ──────────────────────────────────────────────────────
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ─── Mutations ────────────────────────────────────────────────────────
  const [createEvent, { loading: creating }] = useCreateEvent();
  const [updateEvent, { loading: updating }] = useUpdateEvent();
  const [publishEvent, { loading: publishing }] = usePublishEvent();
  const [stopRecurrence, { loading: stoppingRecurrence }] = useStopEventRecurrence();
  const { upload: uploadImage, uploading: uploadingImage } = useImageUpload({
    purpose: "EVENT",
  });

  // ─── Handlers ─────────────────────────────────────────────────────────

  const openCreateDrawer = useCallback(() => {
    setEditingEvent(undefined);
    setDuplicateFrom(undefined);
    setSubmitError(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((event: Event) => {
    setEditingEvent(event);
    setDuplicateFrom(undefined);
    setSubmitError(null);
    setDrawerOpen(true);
  }, []);

  const handleDuplicate = useCallback((event: Event) => {
    setEditingEvent(undefined);
    setDuplicateFrom(event);
    setSubmitError(null);
    setDrawerOpen(true);
  }, []);

  const handlePublish = useCallback((event: Event) => {
    setPublishTarget(event);
  }, []);

  const handleSubmit = useCallback(
    async (input: Record<string, unknown>, imageFile?: File | null) => {
      setSubmitError(null);
      try {
        let imageUrl: string | undefined;

        if (imageFile) {
          const entityId =
            editingEvent?._id ??
            Array.from(crypto.getRandomValues(new Uint8Array(12)))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
          imageUrl = await uploadImage(imageFile, entityId);
          input.photoUrl = imageUrl;
        }

        if (editingEvent) {
          const { data: updateData } = await updateEvent({
            variables: { id: editingEvent._id, input },
          });
          console.info("[EventsPage] Event updated:", editingEvent._id);
          const updated = updateData?.updateEvent as Event | undefined;
          if (updated) {
            setDraftVariant("updated");
            setCreatedDraft(updated);
          } else {
            setDrawerOpen(false);
          }
          setEditingEvent(undefined);
          setDuplicateFrom(undefined);
        } else {
          const { data: createData } = await createEvent({ variables: { input } });
          console.info("[EventsPage] Event created");
          const newEvent = createData?.createEvent as Event | undefined;
          if (newEvent) {
            setDraftVariant("created");
            setCreatedDraft(newEvent);
          } else {
            setDrawerOpen(false);
          }
          setEditingEvent(undefined);
          setDuplicateFrom(undefined);
        }

        refetch();
      } catch (err) {
        console.error("[EventsPage] Submit failed:", err);
        setSubmitError(getSubmitErrorKey(err));
      }
    },
    [editingEvent, uploadImage, updateEvent, createEvent, refetch],
  );

  const handleConfirmPublish = useCallback(async () => {
    if (!publishTarget) return;
    try {
      await publishEvent({ variables: { id: publishTarget._id } });
      console.info("[EventsPage] Event published:", publishTarget._id);
      setPublishTarget(null);
      refetch();
    } catch (err) {
      console.error("[EventsPage] Publish failed:", err);
    }
  }, [publishTarget, publishEvent, refetch]);

  const handleCancel = useCallback((event: Event) => {
    setCancelTarget(event);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    try {
      await updateEvent({
        variables: {
          id: cancelTarget._id,
          input: { status: "CANCELLED" },
        },
      });
      console.info("[EventsPage] Event cancelled:", cancelTarget._id);
      setCancelTarget(null);
      refetch();
    } catch (err) {
      console.error("[EventsPage] Cancel failed:", err);
    }
  }, [cancelTarget, updateEvent, refetch]);

  const handleStopRecurrence = useCallback((event: Event) => {
    setStopRecurrenceTarget(event);
  }, []);

  const handleConfirmStopRecurrence = useCallback(async () => {
    if (!stopRecurrenceTarget) return;
    try {
      await stopRecurrence({
        variables: { id: stopRecurrenceTarget._id },
      });
      console.info(
        "[EventsPage] Recurrence stopped:",
        stopRecurrenceTarget._id,
      );
      setStopRecurrenceTarget(null);
      refetch();
    } catch (err) {
      console.error("[EventsPage] Stop recurrence failed:", err);
    }
  }, [stopRecurrenceTarget, stopRecurrence, refetch]);

  // ─── Delete Draft Handlers ────────────────────────────────────────────

  const handleDeleteDraft = useCallback((event: Event) => {
    setDeleteTarget(event);
  }, []);

  const handleConfirmDeleteDraft = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await updateEvent({
        variables: {
          id: deleteTarget._id,
          input: { status: "CANCELLED" },
        },
      });
      console.info("[EventsPage] Draft deleted:", deleteTarget._id);
      setDeleteTarget(null);
      setCreatedDraft(null);
      setDrawerOpen(false);
      refetch();
    } catch (err) {
      console.error("[EventsPage] Delete draft failed:", err);
    }
  }, [deleteTarget, updateEvent, refetch]);

  // ─── Draft Success View Handlers ─────────────────────────────────────

  const handleSuccessPublish = useCallback(() => {
    if (!createdDraft) return;
    setCreatedDraft(null);
    setDrawerOpen(false);
    setPublishTarget(createdDraft);
  }, [createdDraft]);

  const handleSuccessEdit = useCallback(() => {
    if (!createdDraft) return;
    setCreatedDraft(null);
    setEditingEvent(createdDraft as Event);
    // drawer stays open, now in edit mode
  }, [createdDraft]);

  const handleSuccessDelete = useCallback(() => {
    if (!createdDraft) return;
    setDeleteTarget(createdDraft);
  }, [createdDraft]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setEditingEvent(undefined);
    setDuplicateFrom(undefined);
    setCreatedDraft(null);
    setSubmitError(null);
  }, []);

  // ─── Build Success View ──────────────────────────────────────────────

  function formatDateLong(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString(toBcp47(locale), {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  const successView = createdDraft ? (() => {
    const detailRows = [
      { label: t("shared.draftSuccess.rowName"), value: createdDraft.name },
      { label: t("shared.draftSuccess.rowStartDate"), value: formatDateLong(createdDraft.startDate) },
      { label: t("shared.draftSuccess.rowEndDate"), value: formatDateLong(createdDraft.endDate) },
      ...(createdDraft.startTime
        ? [{ label: t("shared.draftSuccess.rowStartTime"), value: createdDraft.startTime }]
        : []),
      ...(createdDraft.endTime
        ? [{ label: t("shared.draftSuccess.rowEndTime"), value: createdDraft.endTime }]
        : []),
      { label: t("shared.draftSuccess.rowAccess"), value: t("shared.draftSuccess.valueOrgOnly") },
    ];

    const cards = [{ title: t("shared.draftSuccess.cardDetails"), rows: detailRows }];

    // Location card
    const loc = createdDraft.location;
    if (loc) {
      const locRows = [
        {
          label: t("shared.draftSuccess.rowType"),
          value: loc.isVirtual
            ? t("shared.draftSuccess.valueVirtual")
            : t("shared.draftSuccess.valuePhysical"),
        },
      ];
      if (loc.isVirtual) {
        locRows.push({
          label: t("shared.draftSuccess.rowMeetingLink"),
          value: loc.meetingLink || "—",
        });
      } else {
        const address = [loc.address, loc.city, loc.country]
          .filter(Boolean)
          .join(", ");
        locRows.push({ label: t("shared.draftSuccess.rowAddress"), value: address || "—" });
      }
      cards.push({ title: t("shared.draftSuccess.cardLocation"), rows: locRows });
    }

    return (
      <DraftSuccessView
        entityType="event"
        variant={draftVariant}
        name={createdDraft.name}
        photoUrl={createdDraft.photoUrl}
        cards={cards}
        description={createdDraft.description}
        onPublish={handleSuccessPublish}
        onEdit={handleSuccessEdit}
        onDelete={handleSuccessDelete}
        onClose={handleDrawerClose}
      />
    );
  })() : undefined;

  // ─── Guard ────────────────────────────────────────────────────────────
  if (isOrgAdmin && (!currentOrgId || !currentOrg)) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-medium text-foreground">
          {t("common.selectOrg")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("common.selectOrgHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("events.title")}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {currentOrg
              ? t("events.descriptionOrg", { orgName: currentOrg.name })
              : t("events.descriptionAll")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateDrawer}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t("events.createEvent")}
        </button>
      </div>

      {/* Search + Status Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("events.searchPlaceholder")}
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.labelKey}
              type="button"
              onClick={() => handleTabChange(tab.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                statusFilter === tab.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        <RefreshIconButton onRefresh={async () => { await refetch(); }} loading={loading} />
      </div>

      {/* Count */}
      <p className="text-xs text-muted-foreground">
        {t("events.showingCount", { shown: filteredEvents.length, total: totalCount })}
      </p>

      {/* Event Grid */}
      {loading && allEvents.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="h-48 animate-pulse rounded-lg border border-border bg-muted/30"
            />
          ))}
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <p className="text-sm font-medium text-foreground">
            {debouncedSearch || statusFilter
              ? t("events.noMatchTitle")
              : t("events.emptyTitle")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {debouncedSearch || statusFilter
              ? t("events.noMatchHint")
              : t("events.emptyHint")}
          </p>
          <button
            type="button"
            onClick={openCreateDrawer}
            className="mt-4 inline-flex h-8 items-center gap-2 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("events.createEvent")}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredEvents.map((event) => (
            <EventCard
              key={event._id}
              event={event}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onPublish={handlePublish}
              onCancel={handleCancel}
              onDelete={handleDeleteDraft}
              onStopRecurrence={handleStopRecurrence}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Drawer */}
      <CreateEventDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        onSubmit={handleSubmit}
        loading={creating || updating || uploadingImage}
        event={editingEvent}
        duplicateFrom={duplicateFrom}
        currentOrgId={currentOrgId}
        successView={successView}
        error={submitError ? t(submitError) : null}
      />

      {/* Publish Confirmation */}
      {publishTarget && (
        <PublishEventDialog
          isOpen={true}
          onClose={() => setPublishTarget(null)}
          onConfirm={handleConfirmPublish}
          loading={publishing}
          eventName={publishTarget.name}
        />
      )}

      {/* Cancel Confirmation */}
      {cancelTarget && (
        <CancelConfirmDialog
          isOpen={true}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleConfirmCancel}
          loading={updating}
          entityType="event"
          entityName={cancelTarget.name}
        />
      )}

      {/* Stop Recurrence Confirmation */}
      {stopRecurrenceTarget && (
        <StopRecurrenceDialog
          isOpen={true}
          onClose={() => setStopRecurrenceTarget(null)}
          onConfirm={handleConfirmStopRecurrence}
          loading={stoppingRecurrence}
          entityType="event"
          entityName={stopRecurrenceTarget.name}
        />
      )}

      {/* Delete Draft Confirmation */}
      {deleteTarget && (
        <DeleteDraftDialog
          isOpen={true}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleConfirmDeleteDraft}
          loading={updating}
          entityType="event"
          entityName={deleteTarget.name}
        />
      )}
    </div>
  );
}
